import Autocomplete, { autocompleteClasses } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import { observer } from "mobx-react-lite";
import React, { type ReactNode, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AlertContext, { mkAlert } from "../../../stores/contexts/Alert";
import type { Person, Username } from "../../../stores/definitions/Person";
import type PersonModel from "../../../stores/models/PersonModel";
import { sortPeople } from "../../../stores/models/PersonModel";
import useStores from "../../../stores/use-stores";
import RsSet, { nullishToSingleton, unionWith } from "../../../util/set";

type PeopleFieldArgs = {
  onSelection: (person: Person | null, doSearch?: boolean | null) => Promise<void> | void;
  label?: string;
  outsideGroup?: boolean;
  recipient: PersonModel | null;
  excludedUsernames?: RsSet<Username>;
};

/*
 * Autocomplete input field for searching for and selecting a user.
 */
function PeopleField({
  onSelection,
  label,
  outsideGroup = true,
  recipient,
  excludedUsernames,
}: PeopleFieldArgs): ReactNode {
  const { t } = useTranslation("inventory");
  const {
    peopleStore,
    searchStore: { search },
  } = useStores();
  const { addAlert } = useContext(AlertContext);

  const [searchResults, setSearchResults] = useState<RsSet<PersonModel>>(new RsSet<PersonModel>());

  const handleUserChange = (user: Person | null, doSearch?: boolean) => {
    void onSelection(user, doSearch);
  };

  const ownedBy = search.fetcher.ownedBy;
  const owner = search.fetcher.owner;

  useEffect(() => {
    let isMounted = true;
    /* 1 - fetch and store group members */
    if (peopleStore.currentUser) {
      void peopleStore
        .fetchMembersOfSameGroup()
        .then((members) => {
          /* 2 - set owner if defined in URL and not in store yet (direct access / bookmark case) */
          const shouldSetOwner = members && ownedBy && !owner;
          if (isMounted && shouldSetOwner) {
            const ownerToSet = [...members].find((p) => p.username === ownedBy);
            handleUserChange(ownerToSet || null, false);
          }
        })
        .catch((e) => {
          if (e instanceof Error) {
            addAlert(
              mkAlert({
                title: t("peopleField.errors.couldNotGetGroupMembers"),
                message: e.message,
                variant: "error",
              }),
            );
          }
        });
    }
    return () => {
      isMounted = false;
    };
  }, [peopleStore.currentUser]);

  const searchPeople = (searchTerm: string) => {
    if (outsideGroup) {
      peopleStore
        .searchPeople(searchTerm)
        .then((people) => {
          setSearchResults(people);
        })
        .catch(() => {
          setSearchResults(new RsSet());
        });
    }
  };

  const loading = peopleStore.groupMembers === null;
  const allUsers = sortPeople(
    [
      ...unionWith<PersonModel, string>(
        (x: PersonModel) => x.username,
        [
          peopleStore.groupMembers ?? new RsSet<PersonModel>(),
          searchResults,
          nullishToSingleton(peopleStore.currentUser),
        ],
      ).filter((u: PersonModel) => !(excludedUsernames ?? new RsSet<Username>()).has(u.username)),
    ],
    { placeCurrentFirst: true },
  );

  return (
    <Autocomplete<PersonModel>
      sx={{
        maxWidth: 500,
        [`& .${autocompleteClasses.option}`]: { cursor: "default" },
      }}
      options={allUsers}
      groupBy={(u: PersonModel) => u.groupByLabel}
      getOptionLabel={(u: Person) => u.label}
      renderInput={({ slotProps: inputSlotProps, ...rest }) => (
        <TextField
          {...rest}
          variant="outlined"
          autoFocus
          slotProps={{
            ...inputSlotProps,
            input: {
              ...inputSlotProps?.input,
              ...(label !== undefined
                ? {
                    startAdornment: <InputAdornment position="start"> {label}</InputAdornment>,
                  }
                : {}),
              endAdornment: (
                <>
                  {loading && <CircularProgress color="inherit" size={20} />}
                  {inputSlotProps?.input.endAdornment ?? null}
                </>
              ),
            },
          }}
        />
      )}
      size="small"
      value={recipient}
      onChange={(_: React.SyntheticEvent, user: Person | null) => handleUserChange(user)}
      onInputChange={(_: React.SyntheticEvent, searchTerm: string) => searchPeople(searchTerm)}
      isOptionEqualToValue={(option: Person, value: Person) => option.username === value.username}
      openOnFocus
      loading={loading}
    />
  );
}

export default observer(PeopleField);
